import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    useLocation,
    useMatch,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getCatalogItemsShowDetailedView,
    getNewAddedItemsTotal,
    getSelectedCatalogItem,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { ReviewOrderButton } from 'features/AddItemsToOrder';
import { addItemsToOrderIsLoading } from 'features/AddItemsToOrder/model/selectors/addItemsToOrderSelectors';
import { AddItemsBackFromSearchButton } from 'features/SearchCatalogItems';
import { getSearchCatalogItemIsActive } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import {
    getRouteCatalogListAddItemsInventoryItem,
    getRouteOrderCatalogInventoryItem,
} from 'shared/const/router';
import { useIsListsAddItemsFlow } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFeatureFlag } from 'shared/lib/hooks/useFeatureFlag';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { ReviewCatalogSelectionFooter } from 'shared/ui/ReviewCatalogSelectionFooter';
import {
    getInventoryTableItems,
    getInventoryTableItemsLoading,
} from 'widgets/InventoryTable/model/selectors/catalogItemSelectors';
import { fetchInventoryItems } from 'widgets/InventoryTable/model/services/fetchCatalogItems/fetchInventoryItems';
import {
    PropertyCatalogItemDataDefiner,
    PropertyCatalogItemsMoveTo,
} from 'widgets/PropertyCatalog';
import { SearchCatalogItems } from 'widgets/SearchCatalogItems';

import { OrderInventoryTableRow } from './OrderInventoryTableRow';
import { OrderInventoryTableRowSkeleton } from './OrderInventoryTableRowSkeleton';

import { columnWidths } from '../../lib/inventoryTableRowSpec';
import { InventoryTableEmpty } from '../InventoryTable/InventoryTableEmpty';

interface OrderInventoryTableProps {
    /** When true, omit the sticky review footer (parent owns the CTA). */
    hideFooter?: boolean;
}

export const OrderInventoryTable = ({
    hideFooter = false,
}: OrderInventoryTableProps) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isListsAddItems = useIsListsAddItemsFlow();
    const params = useParams();
    const data = useSelector(getInventoryTableItems);

    const catalogPricesVisible = useFeatureFlag('catalog_prices_is_visible');

    const currentPageIsOrders = useMatch('/orders' + '/*');
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedItem = useSelector(getSelectedCatalogItem);
    const detailedViewIsShown = useSelector(getCatalogItemsShowDetailedView);
    const isActiveSearch = useSelector(getSearchCatalogItemIsActive);
    const catalogItemsListLoading = useSelector(getCatalogItemsAreLoading);
    const inventoryListLoading = useSelector(getInventoryTableItemsLoading);
    const isLoading = isActiveSearch
        ? catalogItemsListLoading
        : inventoryListLoading;
    const catalogItems = useSelector(getCatalogItems) as CatalogItem[];
    const selectedPropertyCatalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedCount = useSelector(getNewAddedItemsTotal);
    const canSeePrices =
        useSelector(getUserCanSeePrices) && catalogPricesVisible;
    const isLoadingUpdateOrder = useSelector(addItemsToOrderIsLoading);

    const inventoryOnlyItems = useMemo(() => {
        return catalogItems
            ? catalogItems.filter((item) => item.isInventory)
            : [];
    }, [catalogItems]);

    const dataToDisplay = isActiveSearch ? inventoryOnlyItems : data;

    const showNoInventorySearchResultsHelper =
        isActiveSearch && !isLoading && dataToDisplay.length === 0;

    const orderType = location.state?.orderType;
    const catalogId =
        location.state?.selectedPropertyCatalogId || selectedPropertyCatalogId;

    useInitialEffect(async () => {
        await dispatch(fetchInventoryItems(catalogId));
    });

    const onExpandRow = useCallback(
        (item: CatalogItem) => () => {
            dispatch(catalogItemActions.setSelectedItem(item));
            dispatch(catalogItemActions.setShowDetailedView(true));

            if (isListsAddItems && params.propertyId && params.listId) {
                navigate(
                    getRouteCatalogListAddItemsInventoryItem(
                        params.propertyId,
                        params.listId,
                        item.id,
                    ),
                );
                return;
            }

            navigate(getRouteOrderCatalogInventoryItem(item.id), {
                state: {
                    selectedOrderId,
                    selectedPropertyCatalogId,
                    selectedPropertyId,
                    fromOrder: selectedOrderId,
                },
            });
        },
        [
            dispatch,
            isListsAddItems,
            navigate,
            params.listId,
            params.propertyId,
            selectedOrderId,
            selectedPropertyCatalogId,
            selectedPropertyId,
        ],
    );

    const moveTo = isListsAddItems
        ? PropertyCatalogItemsMoveTo.CUSTOM_LIST
        : PropertyCatalogItemsMoveTo.ORDER;

    return (
        <Box
            sx={
                hideFooter
                    ? {
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          minHeight: 0,
                          height: '100%',
                      }
                    : undefined
            }
        >
            {detailedViewIsShown && selectedItem ? (
                <Box
                    sx={{
                        marginTop: '10px',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <PropertyCatalogItemDataDefiner
                        isDetailed
                        item={selectedItem}
                        isOrderPage={!!currentPageIsOrders}
                    />
                </Box>
            ) : (
                <>
                    {isActiveSearch ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            <SearchCatalogItems
                                catalogId={catalogId}
                                moveTo={moveTo}
                                isInventory
                                hideField
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                padding: '8px 16px',
                                backgroundColor: 'rgba(233, 232, 232, 0.30)',
                                borderRadius: hideFooter
                                    ? '8px 8px 0 0'
                                    : '8px',
                                ...(hideFooter
                                    ? { flex: 1, minHeight: 0 }
                                    : { height: 'calc(100vh - 466px)' }),
                            }}
                        >
                            <Box
                                data-testid="Order-inventory-table"
                                sx={{
                                    overflowY: 'auto',
                                    ...(hideFooter
                                        ? { height: '100%' }
                                        : { height: 'calc(100vh - 482px)' }),
                                }}
                            >
                                {showNoInventorySearchResultsHelper ? (
                                    <InventoryTableEmpty isActiveSearch />
                                ) : (
                                    <Table
                                        sx={{
                                            '& .MuiTableCell-root': {
                                                borderBottom: 'none',
                                            },
                                            borderCollapse: 'separate',
                                            borderSpacing: '0 8px',
                                        }}
                                    >
                                        <TableHead
                                            sx={{
                                                '& th': {
                                                    paddingTop: 0,
                                                    paddingBottom: 0,
                                                },
                                            }}
                                        >
                                            <TableRow>
                                                <TableCell
                                                    data-testid="inventory-title-label"
                                                    sx={{
                                                        paddingLeft: '16px',
                                                        paddingRight: '16px',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                    >
                                                        {t('Item')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    data-testid="inventory-category-label"
                                                    align="left"
                                                    width={
                                                        columnWidths.category
                                                    }
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                    >
                                                        {t('Category')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    data-testid="inventory-uom-label"
                                                    align="center"
                                                    width={columnWidths.uom}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                    >
                                                        {t('UOM')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    data-testid="inventory-min-on-hand-label"
                                                    align="center"
                                                    width={
                                                        columnWidths.minOnHand
                                                    }
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                    >
                                                        {t('Min/Hand')}
                                                    </Typography>
                                                </TableCell>
                                                {canSeePrices && (
                                                    <TableCell
                                                        data-testid="inventory-price-label"
                                                        align="right"
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={600}
                                                        >
                                                            {t('Price')}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    width={
                                                        columnWidths.orderAdd
                                                    }
                                                />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isLoading
                                                ? Array.from({ length: 3 }).map(
                                                      (_, index) => (
                                                          <OrderInventoryTableRowSkeleton
                                                              key={index}
                                                              canSeePrices={
                                                                  canSeePrices
                                                              }
                                                          />
                                                      ),
                                                  )
                                                : dataToDisplay?.map((row) => (
                                                      <OrderInventoryTableRow
                                                          key={row.id}
                                                          item={row}
                                                          canSeePrices={
                                                              canSeePrices
                                                          }
                                                          onExpandRow={onExpandRow(
                                                              row,
                                                          )}
                                                      />
                                                  ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </Box>
                    )}
                    {!hideFooter ? (
                        <ReviewCatalogSelectionFooter
                            selectedCount={selectedCount}
                            leadingAction={
                                isActiveSearch ? (
                                    <AddItemsBackFromSearchButton isInventory />
                                ) : undefined
                            }
                        >
                            <ReviewOrderButton
                                isLoadingUpdateOrder={isLoadingUpdateOrder}
                                orderType={orderType}
                            />
                        </ReviewCatalogSelectionFooter>
                    ) : null}
                </>
            )}
        </Box>
    );
};
